import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useScrollSnap } from '../../hooks/useScrollSnap';
import styles from './Profile.module.scss';
import {
    IonPage,
    IonContent,
    IonList, IonItem, IonLabel, IonButton,
    IonGrid, IonText, IonCard, IonCardHeader, IonCardContent, IonCardTitle,
    IonIcon, useIonRouter, IonModal, IonAlert, IonInput,
    IonLoading, IonToast
} from '@ionic/react';
import type { Contact } from '@rsmaids/shared/types/contacts';
import { chevronBack, person, mail, call, business, briefcase, key, settings, camera, image, closeOutline, logOutOutline, shieldCheckmarkOutline, trashOutline, searchOutline } from 'ionicons/icons';
import { proxyImage, getDefaultAvatar } from '@rsmaids/shared/utils/avatarUtils';
import { useAuth } from '../../contexts/auth-context-core';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { rsAuth } from '@rsmaids/shared/services/rsAuthClient';
import { callAdminApi } from '@rsmaids/shared/services/api/wixAdminApi';
import BasePageWithHeader, { TitleCard } from '../../components/BasePageWithHeader';
import SearchModal from '../../components/SearchModal';
import SheetModal from '../../components/SheetModal';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { useTranslation } from 'react-i18next';

// pickImageBroad shows Android's real native chooser (the OS "Open with..."
// dialog), filtered to only apps that register an image GET_CONTENT handler
// — Google Photos, OneDrive, Samsung Gallery/My Files, etc. — instead of
// @capacitor/camera's CameraSource.Photos, which only launches the modern
// restrictive Photo Picker (local media + Google Photos only, no OneDrive).
// See InlineCameraPlugin.java's pickImageBroad() for the full rationale.
interface InlineCameraPickerPlugin {
    pickImageBroad(): Promise<{ dataUrl: string }>;
}
const InlineCameraPicker: InlineCameraPickerPlugin =
    (window as unknown as { Capacitor?: { Plugins?: Record<string, unknown> } })?.Capacitor?.Plugins?.['InlineCamera'] as InlineCameraPickerPlugin
    ?? registerPlugin<InlineCameraPickerPlugin>('InlineCamera');

interface ProfileProps {
    onClose?: () => void;
}

// i18n-ignore: CSS class names, not text
const TOAST_CLASS_OK = 'app-toast';
// i18n-ignore: CSS class names, not text
const TOAST_CLASS_ERROR = 'app-toast app-toast--error';

const ProfilePage: React.FC<ProfileProps> = ({ onClose }) => {
  const { t } = useTranslation();
    const router = useIonRouter();
    const { user, authState, actions } = useAuth();
    const { logout } = actions;

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        } else if (router.canGoBack()) {
            router.goBack();
        } else {
            router.push('/tabs/dashboard');
        }
    }, [onClose, router]);

    // Refs for scroll animation
    const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
    const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
    const containerRef = useRef<HTMLIonGridElement>(null);

    const [showSearchModal, setShowSearchModal] = useState(false);
    const [showAvatarModal, setShowAvatarModal] = useState(false);
    const [showBasicInfoModal, setShowBasicInfoModal] = useState(false);
    const [showLoginInfoModal, setShowLoginInfoModal] = useState(false);
    const [showChangeEmail, setShowChangeEmail] = useState(false);
    // The change waits here for the person to read what will happen and accept it
    const [pendingEmailChange, setPendingEmailChange] = useState<{ newEmail: string; currentPassword: string } | null>(null);
    const [showLogoutAlert, setShowLogoutAlert] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [passwordToast, setPasswordToast] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [avatarKey, setAvatarKey] = useState(0); // Force re-render of avatar
    const [firstNameDraft, setFirstNameDraft] = useState('');
    const [lastNameDraft, setLastNameDraft] = useState('');

    const handleChangePassword = useCallback(async (data: {
        currentPassword?: string;
        newPassword?: string;
        confirmPassword?: string;
    }) => {
        const currentPassword = (data.currentPassword || '').trim();
        const newPassword = (data.newPassword || '').trim();
        const confirmPassword = (data.confirmPassword || '').trim();

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordToast(t('pages.dashboard.Profile.pleaseFillInAllPasswordFields'));
            return;
        }
        if (newPassword.length < 6) {
            setPasswordToast(t('pages.dashboard.Profile.newPasswordMustBeAtLeast'));
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordToast(t('pages.dashboard.Profile.newPasswordsDoNotMatch'));
            return;
        }
        if (newPassword === currentPassword) {
            setPasswordToast(t('pages.dashboard.Profile.newPasswordMustBeDifferentFrom'));
            return;
        }

        try {
            const result = await rsAuth.changePassword(currentPassword, newPassword);
            if (result.success) {
                setShowChangePassword(false);
                setPasswordToast(t('pages.dashboard.Profile.passwordChangedSuccessfully'));
            } else {
                setPasswordToast(result.error || t('pages.dashboard.Profile.failedToChangePassword'));
            }
        } catch (err) {
            console.error('[Profile] Change password error:', err);
            setPasswordToast(t('pages.dashboard.Profile.anErrorOccurredWhileChangingYour'));
        }
    }, [t]);

    const handleChangeEmail = useCallback(async (data: {
        newEmail?: string;
        confirmEmail?: string;
        currentPassword?: string;
    }) => {
        const newEmail = (data.newEmail || '').trim();
        const confirmEmail = (data.confirmEmail || '').trim();
        const currentPassword = (data.currentPassword || '').trim();

        if (!newEmail || !confirmEmail || !currentPassword) {
            setPasswordToast(t('pages.dashboard.Profile.pleaseFillInAllEmailChange'));
            return;
        }
        if (!/^\S+@\S+\.\S+$/.test(newEmail)) {
            setPasswordToast(t('pages.dashboard.Profile.pleaseEnterAValidNewEmail'));
            return;
        }
        if (newEmail !== confirmEmail) {
            setPasswordToast(t('pages.dashboard.Profile.newEmailAddressesDoNotMatch'));
            return;
        }
        if (newEmail.toLowerCase() === (user?.email || '').toLowerCase()) {
            setPasswordToast(t('pages.dashboard.Profile.newEmailMustBeDifferentFrom'));
            return;
        }

        // Show what will happen first; nothing is sent or changed until they accept
        setPendingEmailChange({ newEmail, currentPassword });
        setShowChangeEmail(false);
    }, [user?.email, t]);

    // Accepted: a verification link goes to the NEW address and the person is signed out. Firebase only switches the
    // sign-in email once that link is clicked; until then the current email keeps working.
    const confirmEmailChange = useCallback(async () => {
        if (!pendingEmailChange) return;
        const { newEmail, currentPassword } = pendingEmailChange;
        setPendingEmailChange(null);

        try {
            const auth = (await import('firebase/auth')).getAuth();
            const authUser = auth.currentUser;
            if (!authUser || !authUser.email) {
                setPasswordToast(t('pages.dashboard.Profile.youMustBeSignedInTo'));
                return;
            }

            const { EmailAuthProvider, reauthenticateWithCredential, verifyBeforeUpdateEmail } = await import('firebase/auth');

            const credential = EmailAuthProvider.credential(authUser.email, currentPassword);
            await reauthenticateWithCredential(authUser, credential);
            await verifyBeforeUpdateEmail(authUser, newEmail);

            setShowLoginInfoModal(false);
            await logout();
            router.push('/login', 'root', 'replace');
        } catch (err) {
            console.error('[Profile] Change email error:', err);
            const code = (err as { code?: string })?.code;
            if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
                setPasswordToast(t('pages.dashboard.Profile.yourCurrentPasswordIsIncorrect'));
                return;
            }
            if (code === 'auth/email-already-in-use') {
                setPasswordToast(t('pages.dashboard.Profile.thatEmailIsAlreadyInUse'));
                return;
            }
            if (code === 'auth/invalid-email') {
                setPasswordToast(t('pages.dashboard.Profile.pleaseEnterAValidEmail'));
                return;
            }
            setPasswordToast(t('pages.dashboard.Profile.failedToChangeEmailPleaseTry'));
        }
    }, [pendingEmailChange, logout, router, t]);

    const handleLogout = async () => {
        try {
            await logout(); // This will clear auth data from context

            // Redirect to login page
            router.push('/login', 'root', 'replace');
        } catch {
            // Silent error handling
        }
    };

    const applyProgress = useCallback((progress: number, _smooth: boolean) => {
        baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
        if (titleCardAnimationRef.current) {
            titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
            titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
        }
    }, []);

    const handleScroll = useScrollSnap(containerRef, 66, applyProgress);

    const handleAvatarChange = async (option: 'camera' | 'gallery') => {
        if (option === 'camera' && !Capacitor.isNativePlatform()) {
            setShowAvatarModal(false);
            setErrorMessage(t('pages.dashboard.Profile.cameraCaptureIsAvailableOnlyIn'));
            return;
        }

        setShowAvatarModal(false);
        setLoading(true);

        try {
            let dataUrl: string | undefined;

            if (option === 'gallery' && Capacitor.isNativePlatform()) {
                const result = await InlineCameraPicker.pickImageBroad();
                dataUrl = result.dataUrl;
            } else {
                const image = await Camera.getPhoto({
                    quality: 100,
                    allowEditing: true,
                    resultType: CameraResultType.DataUrl,
                    source: CameraSource.Camera,
                    webUseInput: false,
                    width: 1024, // High resolution for better quality
                    height: 1024
                });
                dataUrl = image.dataUrl;
            }

            if (dataUrl && user?.wixContactId) {
                console.log('Starting avatar update for contact:', user.wixContactId);

                // Detect the MIME type from the data URL (prefer PNG for better quality)
                const mimeTypeMatch = dataUrl.match(/^data:(image\/[a-zA-Z+]+);base64,/);
                const mimeType = mimeTypeMatch ? mimeTypeMatch[1] : 'image/png';
                console.log('Detected MIME type:', mimeType);

                // Get the current contact to get the revision number
                const response = await callAdminApi('queryContacts', 'contacts', {
                    filter: { _id: { $eq: user.wixContactId } },
                    limit: 1
                });

                console.log('Query response:', response);

                if (response._items && response._items.length > 0) {
                    const existingContact = response._items[0] as {
                        revision?: number;
                        info?: Record<string, unknown> & { picture?: string };
                    };

                    console.log('Existing contact revision:', existingContact.revision);
                    console.log('Current picture:', existingContact.info?.picture);
                    console.log('Existing contact info:', existingContact.info);

                    // If there's an existing avatar, we should delete it to avoid creating duplicates
                    // But for now, just upload the new one and it will overwrite in the UI

                    // Step 1: Upload avatar to Wix Media ContactAvatars folder using service
                    console.log('📤 Step 1: Uploading avatar to Wix Media ContactAvatars folder...');
                    const dataService = DataServiceFactory.getService();
                    const uploadResult = await dataService.contacts.uploadContactAvatar(
                        user.wixContactId,
                        dataUrl,
                        mimeType
                    );

                    console.log('✅ Avatar uploaded to Wix Media:', uploadResult);

                    // Step 2: Update contact with the Wix Media URL
                    console.log('📤 Step 2: Updating contact with Wix Media URL...');
                    const updateInfo = {
                        picture: {
                            image: uploadResult.fileUrl
                        }
                    };

                    const updateResult = await callAdminApi('updateContact', 'contacts', {
                        contactId: user.wixContactId,
                        info: updateInfo,
                        revision: existingContact.revision,
                        options: undefined
                    });

                    console.log('✅ Contact updated with avatar:', updateResult);

                    // Use the uploaded file URL directly
                    const newAvatarUrl = uploadResult.fileUrl;
                    console.log('✅ New avatar URL:', newAvatarUrl);

                    // Update user session storage with new avatar
                    const userSession = sessionStorage.getItem('user');
                    if (userSession) {
                        try {
                            const parsedUser = JSON.parse(userSession);
                            parsedUser.avatarUrl = newAvatarUrl; // Use the uploaded URL directly
                            sessionStorage.setItem('user', JSON.stringify(parsedUser));
                            console.log('✅ Updated user session with new avatar');
                        } catch (e) {
                            console.error('Failed to update user session:', e);
                        }
                    }

                    // Refresh the auth context to pick up the new avatar from session storage
                    await actions.refreshUserData();
                    console.log('✅ Refreshed auth context user data');

                    // Wait a moment for the backend to process
                    await new Promise(resolve => setTimeout(resolve, 500));

                    // Silently refresh the contact data in state
                    const refreshedContact = await DataServiceFactory.getService().getContactById(user.wixContactId);
                    console.log('Refreshed contact data:', refreshedContact);

                    if (refreshedContact) {
                        const dataWithTimestamp = { ...refreshedContact, _lastFetched: new Date().getTime() };
                        setContactData(dataWithTimestamp);
                        localStorage.setItem('userContactData', JSON.stringify(dataWithTimestamp));
                    }

                    // Force avatar re-render by updating the key
                    setAvatarKey(prev => prev + 1);
                }
            }
        } catch (error) {
            console.error('Error capturing image:', error);
            const errorMessage = error instanceof Error ? error.message : t('pages.dashboard.Profile.unknownError');
            if (errorMessage !== 'User cancelled photos app') {
                setErrorMessage(t('pages.dashboard.Profile.failedToUpdateAvatarPleaseTry'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper to get contact data from localStorage
    const getContactData = () => {
        try {
            const stored = localStorage.getItem('userContactData');
            if (stored) {
                const parsed = JSON.parse(stored);
                const now = Date.now();
                const lastFetched = parsed._lastFetched || 0;
                // Refetch if data is older than 5 minutes
                if (now - lastFetched > 5 * 60 * 1000) {
                    return null; // Force refetch
                }
                return parsed;
            }
        } catch {
            // Silent error
        }
        return null;
    };


    const [contactData, setContactData] = useState<Contact | null>(() => getContactData());

    const openBasicInfoModal = useCallback(() => {
        const fullName = (user?.name || '').trim();
        if (contactData?.firstName || contactData?.lastName) {
            setFirstNameDraft(contactData?.firstName || '');
            setLastNameDraft(contactData?.lastName || '');
        } else {
            const parts = fullName.split(/\s+/).filter(Boolean);
            setFirstNameDraft(parts[0] || '');
            setLastNameDraft(parts.slice(1).join(' '));
        }
        setShowBasicInfoModal(true);
    }, [user?.name, contactData?.firstName, contactData?.lastName]);

    const handleSaveBasicInfo = useCallback(async () => {
        const first = firstNameDraft.trim();
        const last = lastNameDraft.trim();
        if (!first && !last) {
            setPasswordToast(t('pages.dashboard.Profile.pleaseEnterAtLeastAFirst'));
            return;
        }
        if (!user?.wixContactId) {
            setPasswordToast(t('pages.dashboard.Profile.contactNotFoundForThisAccount'));
            return;
        }

        setLoading(true);
        try {
            const response = await callAdminApi('queryContacts', 'contacts', {
                filter: { _id: { $eq: user.wixContactId } },
                limit: 1,
            });

            const revision = (response as { _items?: Array<{ revision?: number }> })?._items?.[0]?.revision;

            await callAdminApi('updateContact', 'contacts', {
                contactId: user.wixContactId,
                info: {
                    name: {
                        first,
                        last,
                    },
                },
                revision,
                options: undefined,
            });

            const refreshed = await DataServiceFactory.getService().getContactById(user.wixContactId);
            if (refreshed) {
                const data = { ...refreshed, _lastFetched: Date.now() };
                setContactData(data);
                localStorage.setItem('userContactData', JSON.stringify(data));
            }

            const sessionUser = sessionStorage.getItem('user');
            if (sessionUser) {
                try {
                    const parsed = JSON.parse(sessionUser);
                    parsed.name = `${first} ${last}`.trim();
                    sessionStorage.setItem('user', JSON.stringify(parsed));
                } catch {
                    // Silent JSON parse errors
                }
            }

            await actions.refreshUserData();
            setShowBasicInfoModal(false);
            setPasswordToast(t('pages.dashboard.Profile.basicInfoUpdatedSuccessfully'));
        } catch (err) {
            console.error('[Profile] Save basic info error:', err);
            setPasswordToast(t('pages.dashboard.Profile.failedToSaveBasicInfoPlease'));
        } finally {
            setLoading(false);
        }
    }, [actions, firstNameDraft, lastNameDraft, user?.wixContactId, t]);

    // Always show all standard fields in a fixed order
    const orderedFields: [string, string][] = [
        ['phone', 'Phone:'],
        ['address', 'Address:'],
        ['company', 'Company:'],
        ['jobTitle', 'Job Title:']
    ];

    const avatarMenuOptions = [
        {
            label: t('pages.dashboard.Profile.takePhoto'),
            icon: camera,
            onClick: () => handleAvatarChange('camera'),
        },
        {
            label: t('pages.dashboard.Profile.chooseFromGallery'),
            icon: image,
            onClick: () => handleAvatarChange('gallery'),
        },
    ];

    // Fetch contact data if localStorage is empty
    useEffect(() => {
        const fetchContactDataIfNeeded = async () => {
            if (!contactData && user?.wixContactId) {
                try {
                    const profile = await DataServiceFactory.getService().getContactById(user.wixContactId);
                    if (profile) {
                        const dataWithTimestamp = { ...profile, _lastFetched: Date.now() };
                        setContactData(dataWithTimestamp);
                        try {
                            localStorage.setItem('userContactData', JSON.stringify(dataWithTimestamp));
                        } catch {
                            // Silent storage error
                        }
                    }
                } catch {
                    // Silent error
                }
            }
        };
        fetchContactDataIfNeeded();
    }, [user?.wixContactId, contactData]);

    if (!authState?.isLoggedIn || !user) {
        const content = (
            <IonContent className="ion-text-center ion-padding">
                <IonText>{t('pages.dashboard.Profile.pleaseLogInToViewYour')}</IonText>
            </IonContent>
        );
        return onClose ? <div className={styles.modalWrapper}>{content}</div> : <IonPage className={styles.page}>{content}</IonPage>;
    }

    const pageContent = (
        <BasePageWithHeader
            ref={baseHeaderRef}
            title={t('pages.dashboard.Profile.myProfile')}
            toolbarClassName={styles.toolbar}
            smallTitleClassName={styles.smallTitle}
            titleHeaderClassName={styles.titleHeader}
            startButtons={
                onClose ? (
                    <IonButton shape='round' className={styles.closeButton} onClick={onClose}>
                        <IonIcon icon={closeOutline} />
                    </IonButton>
                ) : (
                    <IonButton shape='round' className={styles.backButton} onClick={handleClose}>
                        <IonIcon icon={chevronBack} />
                    </IonButton>
                )
            }
            endButtons={
                <IonButton shape='round' className={styles.searchButton} onClick={() => setShowSearchModal(true)}>
                    <IonIcon icon={searchOutline} />
                </IonButton>
            }
        >
            <IonContent scrollY={false} className={styles.profileContent}>
                <IonGrid
                    ref={containerRef}
                    className={styles.content}
                    onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop)}
                >
                    <TitleCard
                        ref={titleCardAnimationRef}
                        title={t('pages.dashboard.Profile.myProfile')}
                        cardClassName={styles.profileCard}
                        titleContainerClassName={styles.titleContainer}
                        titleClassName={styles.profileTitle}
                    />

                    {/* Contact Information */}
                    <IonCard className={styles.sectionCard}>
                        <IonCardHeader>
                            <IonCardTitle className={styles.sectionTitle}>
                                <IonIcon icon={person} className={styles.sectionIcon} />{t('pages.dashboard.Profile.contactInformation')}
                            </IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <div className={styles.heroCompact}>
                                <div className={styles.avatarContainer}>
                                    <div className={styles.profileAvatar} key={avatarKey}>
                                        {user?.avatarUrl ? (
                                            <img
                                                src={proxyImage(user.avatarUrl)}
                                                alt={user?.name || t('pages.dashboard.Profile.user')}
                                                onError={(e) => {
                                                    const imgElement = e.target as HTMLImageElement;
                                                    imgElement.src = getDefaultAvatar(user?.name || 'User');
                                                }}
                                            />
                                        ) : (
                                            <img
                                                src={getDefaultAvatar(user?.name || 'User')}
                                                alt={user?.name || t('pages.dashboard.Profile.user')}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div className={styles.identityBlock}>
                                    <h1 className={styles.profileValue}>{user?.name || t('pages.dashboard.Profile.userName')}</h1>
                                    <p className={styles.profileDescription}>
                                        {user?.email || 'user@example.com'}
                                    </p>
                                </div>
                            </div>

                            <IonText color="medium">
                                <p className={styles.sectionDescription}>{t('pages.dashboard.Profile.yourPersonalContactDetailsAndBusiness')}
                                </p>
                            </IonText>
                            <IonList className={`${styles.settingsList} ${styles.contactDetailsList}`}>
                                {orderedFields.map(([key, label]: [string, string]) => {
                                    let value = '';
                                    let icon = person;
                                    let iconBadgeVariant = styles.iconBadgePrimary;
                                    if (contactData) {
                                        switch (key) {
                                            case 'phone':
                                                value = contactData.phones?.[0]?.phone || '';
                                                icon = call;
                                                iconBadgeVariant = styles.iconBadgeSecondary;
                                                break;
                                            case 'address': {
                                                const addrObj = contactData.addresses?.[0]?.address;
                                                if (addrObj) {
                                                    value = [
                                                        addrObj.addressLine1,
                                                        addrObj.addressLine2,
                                                        addrObj.city,
                                                        addrObj.state || addrObj.subdivision,
                                                        addrObj.postalCode,
                                                        addrObj.country
                                                    ]
                                                        .filter(Boolean)
                                                        .join(', ');
                                                } else {
                                                    value = contactData.address || '';
                                                }
                                                iconBadgeVariant = styles.iconBadgeTertiary;
                                                break;
                                            }
                                            case 'company':
                                                value = contactData.company || '';
                                                icon = business;
                                                iconBadgeVariant = styles.iconBadgePrimary;
                                                break;
                                            case 'jobTitle':
                                                value = contactData.jobTitle || '';
                                                icon = briefcase;
                                                iconBadgeVariant = styles.iconBadgeSecondary;
                                                break;
                                            default:
                                                value = '';
                                                iconBadgeVariant = styles.iconBadgePrimary;
                                        }
                                    }
                                    return (
                                        <IonItem key={key} lines="none" className={`${styles.settingsItem} ${styles.contactDetailItem}`}>
                                            <div slot="start" className={`${styles.iconBadge} ${iconBadgeVariant}`}>
                                                <IonIcon icon={icon} />
                                            </div>
                                            <IonLabel>
                                                <h3>{label.replace(':', '')}</h3>
                                                <p>{value || t('pages.dashboard.Profile.notProvided')}</p>
                                            </IonLabel>
                                        </IonItem>
                                    );
                                })}
                            </IonList>
                        </IonCardContent>
                    </IonCard>

                    {/* Account Settings */}
                    <IonCard className={styles.sectionCard}>
                        <IonCardHeader>
                            <IonCardTitle className={styles.sectionTitle}>
                                <IonIcon icon={settings} className={styles.sectionIcon} />{t('pages.dashboard.Profile.accountSettings')}
                            </IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <IonList className={styles.settingsList}>
                                <IonItem
                                    button
                                    detail
                                    className={styles.settingsItem}
                                    onClick={openBasicInfoModal}
                                >
                                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                                        <IonIcon icon={person} />
                                    </div>
                                    <IonLabel>
                                        <h3>{t('pages.dashboard.Profile.basicInfo')}</h3>
                                        <p>{t('pages.dashboard.Profile.editYourNameAndProfileImage')}</p>
                                    </IonLabel>
                                </IonItem>

                                <IonItem
                                    button
                                    detail
                                    className={styles.settingsItem}
                                    onClick={() => setShowLoginInfoModal(true)}
                                >
                                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                                        <IonIcon icon={key} />
                                    </div>
                                    <IonLabel>
                                        <h3>{t('pages.dashboard.Profile.loginInfo')}</h3>
                                        <p>{t('pages.dashboard.Profile.changeYourEmailAndPassword')}</p>
                                    </IonLabel>
                                </IonItem>

                                <IonItem
                                    button
                                    detail
                                    className={styles.settingsItem}
                                    onClick={() => setPasswordToast(t('pages.dashboard.Profile.2StepVerificationIsManagedIn'))}
                                >
                                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                                        <IonIcon icon={shieldCheckmarkOutline} />
                                    </div>
                                    <IonLabel>
                                        <h3>{t('pages.dashboard.Profile.2StepVerification')}</h3>
                                        <p>{t('pages.dashboard.Profile.addAnExtraLayerOfSign')}</p>
                                    </IonLabel>
                                </IonItem>

                                <IonItem
                                    lines="none"
                                    button
                                    detail
                                    className={styles.settingsItem}
                                    onClick={() => setPasswordToast(t('pages.dashboard.Profile.pleaseContactYourAccountAdministratorFor'))}
                                >
                                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeDanger}`}>
                                        <IonIcon icon={trashOutline} />
                                    </div>
                                    <IonLabel>
                                        <h3>{t('pages.dashboard.Profile.deleteYourAccount')}</h3>
                                        <p>{t('pages.dashboard.Profile.permanentlyDeleteYourAccount')}</p>
                                    </IonLabel>
                                </IonItem>
                            </IonList>
                        </IonCardContent>
                    </IonCard>

                    {/* Logout */}
                    <IonCard className={styles.sectionCard}>
                        <IonCardContent>
                            <IonList className={styles.settingsList}>
                                <IonItem button detail={false} onClick={() => setShowLogoutAlert(true)} className={`${styles.settingsItem} ${styles.logoutItem}`} lines="none">
                                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeDanger}`}>
                                        <IonIcon icon={logOutOutline} />
                                    </div>
                                    <IonLabel>
                                        <h3>{t('pages.dashboard.Profile.logOut')}</h3>
                                        <p>{t('pages.dashboard.Profile.signOutOfYourAccount')}</p>
                                    </IonLabel>
                                </IonItem>
                            </IonList>
                        </IonCardContent>
                    </IonCard>

                    {/* Profile Tips */}
                    <IonCard className={styles.tipsCard}>
                        <IonCardContent>
                            <IonText>
                                <h3 className={styles.tipsTitle}>
                                    <IonIcon icon={person} />{t('pages.dashboard.Profile.profileTips')}
                                </h3>
                                <ul className={styles.tipsList}>
                                    <li>{t('pages.dashboard.Profile.keepYourContactInformationUpTo')}</li>
                                    <li>{t('pages.dashboard.Profile.useAProfessionalProfilePictureTo')}</li>
                                    <li>{t('pages.dashboard.Profile.regularlyUpdateYourPasswordForSecurity')}</li>
                                    <li>{t('pages.dashboard.Profile.reviewYourAccountSettingsPeriodically')}</li>
                                    <li>{t('pages.dashboard.Profile.enableNotificationsForImportantUpdates')}</li>
                                </ul>
                            </IonText>
                        </IonCardContent>
                    </IonCard>
                </IonGrid>
            </IonContent>

            <SearchModal
                isOpen={showSearchModal}
                onClose={() => setShowSearchModal(false)}
            />

            <IonLoading
                isOpen={loading}
                spinner="bubbles"
                message={''}
                showBackdrop={false}
                cssClass={styles.profileLoading}
            />

            <IonToast
                isOpen={!!errorMessage}
                message={errorMessage || ''}
                duration={3000}
                onDidDismiss={() => setErrorMessage(null)}
                color="danger"
            />

            {/* Change password */}
            <IonAlert
                isOpen={showChangePassword}
                cssClass="glass-alert"
                header={t('pages.dashboard.Profile.changePassword')}
                inputs={[
                    { name: 'currentPassword', type: 'password', placeholder: t('pages.dashboard.Profile.currentPassword') },
                    { name: 'newPassword', type: 'password', placeholder: t('pages.dashboard.Profile.newPasswordMin6Characters') },
                    { name: 'confirmPassword', type: 'password', placeholder: t('pages.dashboard.Profile.confirmNewPassword') },
                ]}
                buttons={[
                    { text: t('pages.dashboard.Profile.cancel'), role: 'cancel', handler: () => setShowChangePassword(false) },
                    {
                        text: t('pages.dashboard.Profile.update'),
                        handler: (data) => {
                            // Return false to keep the alert open; we manage dismissal ourselves
                            handleChangePassword(data);
                            return false;
                        },
                    },
                ]}
                onDidDismiss={() => setShowChangePassword(false)}
            />

            <IonAlert
                isOpen={showChangeEmail}
                cssClass="glass-alert"
                header={t('pages.dashboard.Profile.changeEmail')}
                inputs={[
                    { name: 'currentEmail', type: 'text', value: user?.email || '', placeholder: t('pages.dashboard.Profile.currentEmail'), attributes: { readonly: true } },
                    { name: 'newEmail', type: 'email', placeholder: t('pages.dashboard.Profile.newEmailAddress'), attributes: { id: 'change-email-new', autocomplete: 'off' } },
                    { name: 'confirmEmail', type: 'email', placeholder: t('pages.dashboard.Profile.confirmNewEmail'), attributes: { id: 'change-email-confirm', autocomplete: 'off' } },
                    { name: 'currentPassword', type: 'password', placeholder: t('pages.dashboard.Profile.currentPassword'), attributes: { id: 'change-email-password', autocomplete: 'current-password' } },
                ]}
                buttons={[
                    { text: t('pages.dashboard.Profile.cancel'), role: 'cancel', handler: () => setShowChangeEmail(false) },
                    {
                        text: t('pages.dashboard.Profile.change'),
                        handler: (data) => {
                            // Browser autofill fills a field without telling the alert, so its data can be empty: read the fields themselves
                            const field = (id: string, fallback?: string) =>
                                (document.getElementById(id) as HTMLInputElement | null)?.value || fallback;
                            handleChangeEmail({
                                newEmail: field('change-email-new', data.newEmail),
                                confirmEmail: field('change-email-confirm', data.confirmEmail),
                                currentPassword: field('change-email-password', data.currentPassword),
                            });
                            return false;
                        },
                    },
                ]}
                onDidDismiss={() => setShowChangeEmail(false)}
            />

            <IonAlert
                isOpen={!!pendingEmailChange}
                cssClass="glass-alert"
                header={t('pages.dashboard.Profile.confirmEmailChangeTitle')}
                message={t('pages.dashboard.Profile.confirmEmailChangeMessage', { email: pendingEmailChange?.newEmail ?? '' })}
                buttons={[
                    { text: t('pages.dashboard.Profile.cancel'), role: 'cancel', handler: () => setPendingEmailChange(null) },
                    { text: t('pages.dashboard.Profile.sendLinkAndSignOut'), handler: () => { confirmEmailChange(); } },
                ]}
                onDidDismiss={() => setPendingEmailChange(null)}
            />

            <IonToast
                isOpen={!!passwordToast}
                message={passwordToast}
                cssClass={
                    passwordToast === t('pages.dashboard.Profile.passwordChangedSuccessfully') ||
                    passwordToast === t('pages.dashboard.Profile.basicInfoUpdatedSuccessfully')
                        ? TOAST_CLASS_OK
                        : TOAST_CLASS_ERROR
                }
                position={
                    passwordToast === t('pages.dashboard.Profile.passwordChangedSuccessfully') ||
                    passwordToast === t('pages.dashboard.Profile.basicInfoUpdatedSuccessfully')
                        ? 'bottom'
                        : 'top'
                }
                duration={3500}
                onDidDismiss={() => setPasswordToast('')}
            />

            {/* Logout Confirmation Alert */}
            <IonAlert
                isOpen={showLogoutAlert}
                onDidDismiss={() => setShowLogoutAlert(false)}
                header={t('pages.dashboard.Profile.logOut')}
                message={t('pages.dashboard.Profile.areYouSureYouWantTo')}
                cssClass="logout-alert"
                buttons={[
                    {
                        text: t('pages.dashboard.Profile.cancel'),
                        role: 'cancel',
                        handler: () => {
                            setShowLogoutAlert(false);
                        },
                    },
                    {
                        text: t('pages.dashboard.Profile.logOut'),
                        handler: () => {
                            handleLogout();
                        },
                    },
                ]}
            />

            <SearchModal
                isOpen={showSearchModal}
                onClose={() => setShowSearchModal(false)}
            />

            {showBasicInfoModal && (
                <IonModal
                    isOpen={showBasicInfoModal}
                    onDidDismiss={() => setShowBasicInfoModal(false)}
                    handle={false}
                >
                    <div className={styles.modalWrapper}>
                        <BasePageWithHeader
                            title={t('pages.dashboard.Profile.basicInfo')}
                            toolbarClassName={styles.toolbar}
                            smallTitleClassName={`${styles.smallTitle} ${styles.modalTitleVisible}`}
                            titleHeaderClassName={styles.titleHeader}
                            startButtons={
                                <IonButton shape='round' className={styles.closeButton} onClick={() => setShowBasicInfoModal(false)}>
                                    <IonIcon icon={chevronBack} />
                                </IonButton>
                            }
                            endButtons={<div className={styles.headerButtonSpacer} aria-hidden="true" />}
                        >
                            <IonContent className={styles.profileContent}>
                                <IonGrid className={styles.basicInfoContent}>
                                    <div className={styles.basicInfoAvatarWrap}>
                                        <div className={styles.basicInfoAvatar} key={avatarKey}>
                                            {user?.avatarUrl ? (
                                                <img
                                                    src={proxyImage(user.avatarUrl)}
                                                    alt={user?.name || t('pages.dashboard.Profile.user')}
                                                    onError={(e) => {
                                                        const imgElement = e.target as HTMLImageElement;
                                                        imgElement.src = getDefaultAvatar(user?.name || 'User');
                                                    }}
                                                />
                                            ) : (
                                                <img
                                                    src={getDefaultAvatar(user?.name || 'User')}
                                                    alt={user?.name || t('pages.dashboard.Profile.user')}
                                                />
                                            )}
                                        </div>
                                        <IonButton
                                            className={styles.basicInfoAvatarEdit}
                                            fill="solid"
                                            size="small"
                                            onClick={() => setShowAvatarModal(true)}
                                        >
                                            <IonIcon icon={camera} />
                                        </IonButton>

                                        <IonText className={styles.basicInfoNamePreview}>
                                            {`${firstNameDraft} ${lastNameDraft}`.trim() || t('pages.dashboard.Profile.yourName')}
                                        </IonText>
                                    </div>

                                    <div className={styles.basicInfoCard}>
                                        <div className={styles.basicInfoFieldsSection}>
                                            <div className={styles.basicInfoFieldGroup}>
                                                <IonLabel className={styles.basicInfoLabel}>{t('pages.dashboard.Profile.firstName')}</IonLabel>
                                                <IonInput
                                                    className={styles.basicInfoInput}
                                                    value={firstNameDraft}
                                                    placeholder={t('pages.dashboard.Profile.firstName2')}
                                                    onIonInput={(e) => setFirstNameDraft(String(e.detail.value || ''))}
                                                />
                                            </div>

                                            <div className={styles.basicInfoFieldGroup}>
                                                <IonLabel className={styles.basicInfoLabel}>{t('pages.dashboard.Profile.lastName')}</IonLabel>
                                                <IonInput
                                                    className={styles.basicInfoInput}
                                                    value={lastNameDraft}
                                                    placeholder={t('pages.dashboard.Profile.lastName2')}
                                                    onIonInput={(e) => setLastNameDraft(String(e.detail.value || ''))}
                                                />
                                            </div>
                                        </div>
                                        <div className={styles.basicInfoSaveRow}>
                                            <IonButton fill="solid" className={styles.cardSaveButton} onClick={handleSaveBasicInfo}>{t('pages.dashboard.Profile.save')}
                                            </IonButton>
                                        </div>
                                    </div>
                                </IonGrid>
                            </IonContent>
                        </BasePageWithHeader>
                    </div>
                </IonModal>
            )}

            {showLoginInfoModal && (
                <IonModal
                    isOpen={showLoginInfoModal}
                    onDidDismiss={() => setShowLoginInfoModal(false)}
                    handle={false}
                >
                    <div className={styles.modalWrapper}>
                        <BasePageWithHeader
                            title={t('pages.dashboard.Profile.loginInfo')}
                            toolbarClassName={styles.toolbar}
                            smallTitleClassName={`${styles.smallTitle} ${styles.modalTitleVisible}`}
                            titleHeaderClassName={styles.titleHeader}
                            startButtons={
                                <IonButton shape='round' className={styles.closeButton} onClick={() => setShowLoginInfoModal(false)}>
                                    <IonIcon icon={chevronBack} />
                                </IonButton>
                            }
                            endButtons={<div className={styles.headerButtonSpacer} aria-hidden="true" />}
                        >
                            <IonContent className={styles.profileContent}>
                                <IonGrid className={styles.loginInfoContent}>
                                    <IonList className={styles.loginInfoList}>
                                        <IonItem button detail className={styles.loginInfoItem} onClick={() => setShowChangeEmail(true)}>
                                            <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                                                <IonIcon icon={mail} />
                                            </div>
                                            <IonLabel>
                                                <h3>{t('pages.dashboard.Profile.emailAddress')}</h3>
                                                <p>{user?.email || t('pages.dashboard.Profile.notProvided')}</p>
                                            </IonLabel>
                                        </IonItem>

                                        <IonItem button detail lines="none" className={styles.loginInfoItem} onClick={() => setShowChangePassword(true)}>
                                            <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                                                <IonIcon icon={key} />
                                            </div>
                                            <IonLabel>
                                                <h3>{t('pages.dashboard.Profile.password')}</h3>
                                                <p>{t('pages.dashboard.Profile.changeYourPassword')}</p>
                                            </IonLabel>
                                        </IonItem>
                                    </IonList>
                                </IonGrid>
                            </IonContent>
                        </BasePageWithHeader>
                    </div>
                </IonModal>
            )}

            <SheetModal
                isOpen={showAvatarModal}
                onClose={() => setShowAvatarModal(false)}
                title={t('pages.dashboard.Profile.changeAvatar')}
                menuOptions={avatarMenuOptions}
            />
        </BasePageWithHeader>
    );

    return onClose ? <div className={styles.modalWrapper}>{pageContent}</div> : <IonPage className={styles.page}>{pageContent}</IonPage>;
};

export default ProfilePage;